import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams, useRouteMatch } from "react-router-dom";
import {
  cloneLandingPage,
  createPage,
  deletePage,
  invalidatePageChange,
  invalidatePageDelete,
  rearrangePageWidgets,
  updatePageMetadata,
  updatePageVisibility,
} from "actions/pages";
import cx from "classnames";
import _ from "lodash";
import Sticky from "react-sticky-el";
import {
  Button,
  Col,
  Container,
  Nav,
  Row,
  Tab,
  TabContainer,
  Form,
  FormCheck
} from "react-bootstrap";
import { closestCenter, DndContext, DragOverlay } from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { dropAnimationConfig, useDraggableSensors } from "components/Editor/widgets/content/sectionManager/manualSection/dragHelpers";
import StoryboardContext from "components/Storyboard/context";
import { STORYBOARD_TYPES } from "constants/storyboardType";
import { getBaseUrl } from "services/util";
import {
  PageItem,
  SaveMessageModal,
  SortablePageItem,
  SEOBasics,
  SocialShare
} from "../components";
import { SuccessToast } from "components/Toast";
import { toast } from "react-toastify";
import styles from "./pageSettings.module.scss";
import { isIcV2 } from "../preview/icV2/helper";


export default function IcV2PageSettings({ editorSession, type }) {
  const dispatch = useDispatch();
  const history = useHistory();
  let { url } = useRouteMatch();
  let baseUrl = getBaseUrl(url);
  const { id } = useParams();
  const storyboard = React.useContext(StoryboardContext);
  const [state, setState] = useState({
    selected: null,
    selectedGating: null,
    unsavedChanges: false,
    nextPage: null,
  });
  const pages = useSelector((state) => _.get(state, "pages.items", []));
  const activePage = useSelector((state) => _.get(state, `pages.page[${id}].item`, {}));
  const deletion = useSelector((state) => _.get(state, "pages.deletion", {}));
  const creating = useSelector((state) =>
    _.get(state, "pages.creating", false)
  );
  const sensors = useDraggableSensors();
  const [items, setItems] = useState(pages);
  const [activeitem, setActiveItem] = useState(null)
  const [overId, setOverId] = useState(null)

  const handleDragStart = (event) => {
    setActiveItem(event.active.id);
    setOverId(null);
  };

  const handleDragOver = (event) => {
    const { over } = event;
    if (!over) {
      setOverId(null);
      return;
    }
    setOverId(over.id);
  };

  const handlePageClick = (page) => {
    if (id === page.id) return;
    setState((prev) => ({ ...prev, selectedGating: null }));
    dispatch(invalidatePageChange(page.id));
    history.push(`${baseUrl}/pages/${page.id}`);
  };

  const handleDeleteClick = (pageId) => {
    dispatch(deletePage(pageId));
  };

  const handleAddPageClick = (setLoading) => {
    if (creating) return;
    const request = {
      name: `Section ${_.size(pages) + 1}`,
      storyboard: storyboard.id,
      order: _.get(_.maxBy(pages, "order"), "order", 0) + 1,
    };
    dispatch(
      createPage(request, (response) => {
        setLoading?.(false);
        history.push(`${baseUrl}/pages/${response.id}`);
      })
    );
  };

  const handleClonePage = (pageId) => {
    dispatch(
      cloneLandingPage(pageId, (response) => {
        history.push(`${baseUrl}/pages/${response.id}`);
      })
    );
  };

  const handleSaveChanges = () => {
    setState((prev) => ({ ...prev, unsavedChanges: false }));
    return true;
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveItem(null);
    setOverId(null);
    if (!over) return;
    if (active.id !== over.id) {
      const oldIndex = items.findIndex((page) => page.id === active.id);
      const newIndex = items.findIndex((page) => page.id === over.id);
      const orderedItems = arrayMove(items, oldIndex, newIndex);
      const entityOrderList = orderedItems.map(({ id }, index) => ({
        id,
        order: index,
      }));
      setItems(orderedItems);
      dispatch(rearrangePageWidgets(id, { entityOrderList }, orderedItems));
    }
  };

  useEffect(() => {
    if (!_.isEqual(items, pages)) {
      setItems(pages);
    }
  }, [pages]);

  useEffect(() => {
    if (!activePage) return;
    setState((prev) => ({
      ...prev,
      selected: activePage,
    }));
  }, [id]);

  useEffect(() => {
    if (deletion && deletion.deleted && _.get(deletion, "id", null) === id) {
      const nextPage = pages[Math.max(_.get(deletion, "deletedIndex", 0), 0)];
      if (nextPage) {
        dispatch(invalidatePageChange(nextPage.id));
        history.push(`${baseUrl}/pages/${nextPage.id}`);
      }
      dispatch(invalidatePageDelete());
    }
  }, [deletion]);

  return (
    <section className={styles.pageSettings}>
      <div
        className={styles.pages}
        style={{ width: type !== "page" && "300px" }}
      >
        <div className={styles.pagesWrapper}>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={items.map((p) => p.id)}
              strategy={verticalListSortingStrategy}
            >
              {items.map((page) => (
                <SortablePageItem
                  key={page.id}
                  id={page.id}
                  editorSession={editorSession}
                  page={page}
                  pages={items}
                  active={page.id === _.get(activePage, "id")}
                  onClick={handlePageClick}
                  onClone={handleClonePage}
                  onDelete={handleDeleteClick}
                  draggable={isIcV2(storyboard?.type)}
                  isOver={overId == page.id}
                  isActiveItem={activeitem == page.id}
                />
              ))}
            </SortableContext>
            <DragOverlay dropAnimation={dropAnimationConfig}>
              {activeitem ? (
                <PageItem
                  pages={pages}
                  page={pages.find((item) => item.id == activeitem)}
                  active={true}
                  draggable={true}
                />
              ): null}
            </DragOverlay>
          </DndContext>
          <Sticky
            mode="bottom"
            scrollElement={`.${styles.pagesWrapper}`}
            boundaryElement={`.${styles.pageSettings}`}
          >
            <div className={styles.addPageButtonContainer}>
              <AddPageButton handleAddPageClick={handleAddPageClick} />
            </div>
          </Sticky>
        </div>
      </div>
      {state.selected && !state.selectedGating && activePage && type === "page" && (
          <PageSettingForm
            editorSession={editorSession}
            page={activePage}
            value={state.request}
          />
        )}
      <SaveMessageModal
        show={state.unsavedChanges}
        onDiscard={() => {
          if (state.nextPage) handlePageClick(state.nextPage, true);
        }}
        onSave={() => {
          const saved = handleSaveChanges();
          if (saved && state.nextPage) handlePageClick(state.nextPage, true);
        }}
      />
    </section>
  );
}

const getInitialRequest = (page) => ({
  info: {
    name: _.get(page, "name", ""),
    autoCreateAlias: _.get(page, "autoCreateAlias", false),
    subDirectory: _.get(page, "subDirectory", "")
  },
  seo: _.get(page, "draft.seo", {}),
  social: _.get(page, "draft.social", {}),
  twitter: _.get(page, "draft.twitter", {}),
});

const PageSettingForm = ({ editorSession, page }) => {
  const [activeTab, setActiveTab] = useState(tabs.INFO.name);
  const dispatch = useDispatch();
  const [request, setRequest] = useState(() => getInitialRequest(page));

  const handleChange = (type, change) => {
    setRequest((prev) => ({
      ...prev,
      [type]: {
        ...prev[type],
        ...change,
      },
    }));
  };

  const handleSave = () => {
    dispatch(
      updatePageMetadata(page.id, {
        name: request.info.name,
        autoCreateAlias: request.info.autoCreateAlias,
        seo: request.seo,
        social: request.social,
        twitter: request.twitter,
        alias: request.info.subDirectory,
        hidden: page.hidden,
      })
    );
    toast(
      <SuccessToast
        heading={`Success`}
        description={`${request.info.name} settings saved. Please save the storyboard to apply these changes.`}
      />,
      {
        type: toast.TYPE.DEFAULT,
        position: toast.POSITION.BOTTOM_RIGHT,
        autoClose: 3000,
      }
    );
  };

  const handleReset = () => {
    setRequest({
      info: {
        name: _.get(page, "name", ""),
        subDirectory: _.get(page, "subDirectory", ""),
        autoCreateAlias: _.get(page, "autoCreateAlias", false),
      },
      seo: _.get(page, "draft.seo", {}),
      social: _.get(page, "draft.social", {}),
      twitter: _.get(page, "draft.twitter", {}),
    });
  }

  useEffect(() => {
    if (!page) return;
    setRequest(getInitialRequest(page));
  }, [page?.id]);

  useEffect(() => {
    if(!page?.subDirectory) return;
    setRequest((prev) => ({
      ...prev,
      info: {
        ...prev.info,
        subDirectory: _.get(page, "subDirectory", ""),
      },
    }));
  }, [page?.subDirectory]);

  const actionBtns = [
    {
      className: styles.actionButton,
      variant: "secondary",
      type: "reset",
      onClick: handleReset,
      children: "Reset",
    },
    {
      variant: "primary",
      type: "button",
      onClick: handleSave,
      children: "Save Changes",
    },
  ];


                      
  return (
    <div className={cx(styles.pageSettingForm)}>
      <Container fluid>
        <TabContainer
          defaultActiveKey={tabs.INFO.name}
          activeKey={activeTab}
          onSelect={(tab) => setActiveTab(tab)}
        >
          <Row>
            <Col sm={4} className={styles.options}>
              <Nav className="flex-column">
                {Object.values(tabs).map(item => {
                  return (
                    <Nav.Item key={`${item.name}_${page.id}`}>
                      <Nav.Link eventKey={item.name}>{item.displayName}</Nav.Link>
                    </Nav.Item>
                  )
                })}
              </Nav>
            </Col> 
            <Col sm={8} className={styles.pageSettingsDetail}>
              <Tab.Content>
                {Object.values(tabs).map(({ name, Component }) => {
                  return (
                    <Tab.Pane eventKey={name} key={`${name}_${page.id}`}>
                      <Component
                        editorSession={editorSession}
                        page={page}
                        value={request}
                        onChange={(change, socialChange) => {
                          const value = name === tabs.SOCIAL.name ? socialChange : change;
                          handleChange(name, value);
                        }}
                        onSave={handleSave}
                      />
                    </Tab.Pane>
                  );
                })}
                <footer className={styles.buttonContainer}>
                  {actionBtns.map((btnProps) => <Button {...btnProps} />)}
                </footer>
              </Tab.Content>
            </Col>
          </Row>
        </TabContainer>
      </Container>
    </div>
  );
};


export const AddPageButton = ({ handleAddPageClick }) => {
  const [loading, setLoading] = useState(false);
  return (
    <Button
      disabled={loading}
      variant="primary"
      onClick={() => {
        setLoading(true);
        handleAddPageClick(setLoading);
      }}
    >
      <i className={cx("cleverstory-plus", styles.icon)} />
      Add Page
    </Button>
  );
};

export const PageInfo = ({ value, onChange, page }) => {
  const errors = _.get(value, 'errors') || {};
  const alias = _.get(value, 'info.subDirectory', "")
  const autoCreateAlias = _.get(value, 'info.autoCreateAlias', false)
  const pageId = _.get(page, 'id', null)
  const hidden = _.get(page, 'hidden', false)
  const pages = useSelector((state) => _.get(state, "pages.items", []));
  const visiblePages = pages.filter(item => !item.hidden); 
  const canHide = hidden || (visiblePages.length > 1 && !hidden)

  const handleNameChange = (e) => {
    const newName = e.target.value;
    onChange({ name: newName });
  };

  const handleAliasChange = (e) => {
    const value = e.target.value;
    onChange({ subDirectory: value });
  }
  const toggleAutoCreateAlias = () => {
    onChange({
      autoCreateAlias: !autoCreateAlias
    });
  }
  return (
    <>
      <div className={styles.pageInfo}>
        <div className={styles.headerContainer}>
          <h3 className={styles.header}>Page Info</h3>
        </div>
        <Form className={styles.form}>
          <Form.Group controlId="pageName" className={styles.group}>
            <Form.Label className={styles.label}>Page Name</Form.Label>
            <Form.Control
              isInvalid={errors.name}
              className={styles.control}
              type="text"
              name="name"
              placeholder="Enter page name"
              value={_.get(value, 'info.name', '')}
              onChange={handleNameChange}
            />
            <Form.Control.Feedback type="invalid">
              {errors.name || 'please provide a valid name'}
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Group className={styles.group}>
            <Form.Label className={styles.label}>
              Page URL Slug (last part of the URL)
            </Form.Label>
            <Form.Control
              className={styles.control}
              type="text"
              name="alias"
              placeholder="Enter page alias"
              disabled={autoCreateAlias}
              value={alias}
              onChange={handleAliasChange}
            />
            <Form.Check
              className={styles.checkbox}
              type="checkbox"
              name="autoCreateAlias"
              label="Auto generated alias"
              checked={autoCreateAlias}
              onChange={toggleAutoCreateAlias}
            />
          </Form.Group>
          {canHide && (
            <PageVisibilityToggle 
              pageId={pageId} 
              hidden={hidden} 
            />
          )}
        </Form>
      </div>
    </>
  );
};

export const tabs = {
  INFO: {
    name: "info",
    displayName: "Page Info",
    Component: PageInfo
  },
  SEO: { 
    name: "seo",
    displayName: "SEO Basics",
    Component: SEOBasics,
  },
  SOCIAL: {
    name: "social",
    displayName: "Social Share",
    Component: SocialShare,
  },
};

const useOptimisticVisibility = (pageId, hidden, onVisibilityChange) => {
  const inProgress = useSelector((state) => _.get(state, `pages.page[${pageId}].hiding`, false));
  const [optimisticHidden, setOptimisticHidden] = useState(hidden);

  useEffect(() => {
    setOptimisticHidden(hidden);
  }, [hidden]);

  const toggleVisibility = () => {
    if (inProgress) return;
    
    const newValue = !hidden;
    setOptimisticHidden(newValue);
    onVisibilityChange(newValue);
  };

  return {
    checked: inProgress ? optimisticHidden : hidden,
    inProgress,
    toggleVisibility
  };
};

const PageVisibilityToggle = ({ pageId, hidden }) => {
  const dispatch = useDispatch();
  const onVisibilityChange = (isHidden) => dispatch(updatePageVisibility(pageId, isHidden));
  const { checked, inProgress, toggleVisibility } = useOptimisticVisibility(pageId, hidden, onVisibilityChange);

  return (
    <Form.Group
      controlId="visibility"
      className={cx(styles.smallGroup, styles.flexFormGroup)}
    >
      <Form.Label className={styles.inlineLabel}>
        Hide this page
      </Form.Label>
      <Form.Check type="switch" className={styles.switchButton}>
        <FormCheck.Input  
          onChange={toggleVisibility} 
          checked={checked}
          className={cx({ ["loading"]: inProgress })}
        />
      </Form.Check>
    </Form.Group>
  );
};